/**
 * NewsService.ts
 *
 * Service for interacting with the News API
 * Provides methods for fetching news with various filters and caching
 */

import CacheHelper from "../utils/CacheHelper";
import DebugUtils from "../utils/debugUtils";
import { News, Category, Location, Tag } from "../types";
import { toSlug } from "../utils/slugify";

// Define types for request parameters
export interface NewsParams {
  // Sorting parameters (new approach)
  sort_by?: "latest" | "popular" | "views" | "comments" | "trending";

  // Legacy type parameter (for backward compatibility)
  type?: "latest" | "hot" | "popular" | "viral" | "celebrity" | "mostsee";

  // Filtering parameters
  category?: string;
  category_id?: number | string;
  location?: string;
  location_id?: number | string;
  l?: string; // Legacy support for location slug
  search?: string;
  date_from?: string;
  date_to?: string;
  tag?: string;
  tag_id?: number | string;
  is_hot?: boolean;

  // Pagination
  page?: number;
  per_page?: number;
}

// Define API response types
export interface PaginatedResponse<T> {
  status: boolean;
  message: string;
  data: T[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

class NewsService {
  // Base API URL - Updated to use API v1
  private static API_URL = "/api/v1/news";

  // Default cache TTL (10 minutes - increased for better performance)
  private static DEFAULT_CACHE_TTL = 10 * 60 * 1000;

  /**
   * Fetch news with various filters
   *
   * @param params Filter parameters
   * @param useCache Whether to use cache (default: true)
   * @returns Promise with news data
   */
  static async fetchNews(
    params: NewsParams,
    useCache: boolean = true,
  ): Promise<PaginatedResponse<News>> {
    const requestId = Math.random().toString(36).substr(2, 9);
    const startTime = performance.now();

    console.group(`[NewsService:${requestId}] fetchNews called`);
    console.log("Params:", params);
    console.log("Use cache:", useCache);

    const queryParams = new URLSearchParams();

    // Add all params to query string
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value.toString());
        // Backward compatibility: also send 'l' when key is 'location'
        if (key === "location") {
          queryParams.append("l", value.toString());
        }
      }
    });

    const url = `${this.API_URL}?${queryParams.toString()}`;
    // Use sort_by for caching if available, otherwise fallback to type
    const cacheKey = params.sort_by || params.type || "latest";

    console.log("Generated URL:", url);
    console.log("Cache key:", cacheKey);

    // Try to get from cache first if useCache is true
    if (useCache) {
      console.log("Checking cache...");
      const cachedData = CacheHelper.getCachedNews<PaginatedResponse<News>>(
        cacheKey,
        params,
      );
      if (cachedData) {
        console.log("Cache hit! Returning cached data");
        console.groupEnd();
        return cachedData;
      }
      console.log("Cache miss - proceeding with API call");
    }

    try {
      console.log("Starting API request...");

      // Track API call start with DebugUtils
      DebugUtils.trackAPI({
        url,
        method: "GET",
        requestId,
        timestamp: Date.now(),
        category: cacheKey,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error("Request timeout after 15s");
        controller.abort();
      }, 15000); // Increased to 15s timeout

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      clearTimeout(timeoutId);

      console.log("Response received:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        // Enhanced error handling for different status codes
        console.error("Response not OK:", response.status, response.statusText);
        switch (response.status) {
          case 503:
            throw new Error(
              "Service temporarily unavailable - please try again later",
            );
          case 500:
            throw new Error("Server error - please try again later");
          case 404:
            throw new Error("News content not found");
          case 429:
            throw new Error("Too many requests - please wait a moment");
          default:
            throw new Error(
              `Error fetching news: ${response.status} ${response.statusText}`,
            );
        }
      }

      const rawData = await response.json();

      // Transform API v1 response to expected format
      const data: PaginatedResponse<News> = {
        status: rawData.status || rawData.success || false,
        message: rawData.message || 'Success',
        data: rawData.data || [],
        meta: {
          total: rawData.total || 0,
          per_page: rawData.perPage || 20,
          current_page: rawData.currentPage || 1,
          last_page: Math.ceil((rawData.total || 0) / (rawData.perPage || 20))
        }
      };

      console.log("API response data:", {
        status: data.status,
        dataCount: data.data?.length || 0,
        meta: data.meta,
        hasData: !!data.data,
      });

      // Cache the result if successful
      if (data.status && useCache) {
        console.log("Caching successful response");
        CacheHelper.cacheNews(cacheKey, params, data, {
          ttl: this.DEFAULT_CACHE_TTL,
        });
      }

      const duration = performance.now() - startTime;

      // Track successful API call with DebugUtils
      DebugUtils.trackAPI({
        url,
        method: "GET",
        status: response.status,
        duration,
        requestId,
        timestamp: Date.now(),
        category: cacheKey,
      });

      console.log("Request completed successfully");
      console.groupEnd();
      return data;
    } catch (error) {
      const duration = performance.now() - startTime;
      console.error("API request failed:", error);
      console.groupEnd();

      // Track failed API call with DebugUtils
      DebugUtils.trackAPI({
        url,
        method: "GET",
        duration,
        error: error instanceof Error ? error.message : String(error),
        requestId,
        timestamp: Date.now(),
        category: cacheKey,
      });

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new Error("Request timeout - please check your connection");
        }
        if (
          error.message.includes("NetworkError") ||
          error.message.includes("fetch")
        ) {
          throw new Error(
            "Network error - please check your internet connection",
          );
        }
      }
      console.error("Error fetching news:", error);

      // Return empty result instead of throwing for better UX
      return {
        status: false,
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
        data: [],
        meta: {
          total: 0,
          per_page: params.per_page || 20,
          current_page: params.page || 1,
          last_page: 1,
        },
      };
    }
  }

  /**
   * Fetch latest news
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with latest news
   */
  static async fetchLatestNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      sort_by: "latest",
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    return this.fetchNews(params);
  }

  /**
   * Fetch hot news (featured news items)
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with hot news
   */
  static async fetchHotNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      sort_by: "latest", // Sort by latest for hot news
      is_hot: true, // But filter by hot flag
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    try {
      const result = await this.fetchNews(params);

      // If no hot news found, fallback to regular latest news
      if (!result.data || result.data.length === 0) {
        const fallbackParams = { ...params };
        delete fallbackParams.is_hot; // Remove hot filter
        return this.fetchNews(fallbackParams);
      }

      return result;
    } catch (error) {
      // Fallback to latest news on error
      console.warn(
        "Hot news fetch failed, falling back to latest news:",
        error,
      );
      const fallbackParams: NewsParams = {
        sort_by: "latest",
        page,
        per_page: perPage,
      };
      if (locationSlug && locationSlug !== "all") {
        fallbackParams.location = locationSlug;
      }
      return this.fetchNews(fallbackParams);
    }
  }

  /**
   * Fetch popular news (most reactions)
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with popular news
   */
  static async fetchPopularNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      sort_by: "popular",
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    return this.fetchNews(params);
  }

  /**
   * Fetch viral news (viral category)
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with viral news
   */
  static async fetchViralNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      category: "viral", // Filter by viral category
      sort_by: "latest", // Sort by latest by default
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    try {
      // Try viral category first
      const result = await this.fetchNews(params);
      if (result?.data && result.data.length > 0) {
        return result;
      }

      // Fallback 1: try trending sort (without category constraint)
      const trendingParams: NewsParams = {
        sort_by: "trending",
        page,
        per_page: perPage,
      };
      if (locationSlug && locationSlug !== "all") {
        trendingParams.location = locationSlug;
      }
      const trendingResult = await this.fetchNews(trendingParams);
      if (trendingResult?.data && trendingResult.data.length > 0) {
        return trendingResult;
      }

      // Fallback 2: try popular sort
      const popularParams: NewsParams = {
        sort_by: "popular",
        page,
        per_page: perPage,
      };
      if (locationSlug && locationSlug !== "all") {
        popularParams.location = locationSlug;
      }
      return this.fetchNews(popularParams);
    } catch (error) {
      console.warn("Viral news fetch failed with all fallbacks:", error);
      return {
        status: true,
        message: "No viral news found",
        data: [],
        meta: {
          total: 0,
          per_page: perPage,
          current_page: page,
          last_page: 1,
        },
      };
    }
  }

  /**
   * Fetch celebrity news (selebriti/celebrity category with fallback)
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with celebrity news
   */
  static async fetchCelebrityNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    // Try multiple category slugs for backward compatibility
    const categoryAttempts = ["selebriti", "celebrity", "hiburan"];

    for (const categorySlug of categoryAttempts) {
      try {
        const params: NewsParams = {
          category: categorySlug,
          sort_by: "latest",
          page,
          per_page: perPage,
        };

        if (locationSlug && locationSlug !== "all") {
          params.location = locationSlug;
        }

        const result = await this.fetchNews(params);

        // If we get data, return it
        if (result.data && result.data.length > 0) {
          return result;
        }

        // If no data but successful response, continue to next attempt
        if (result.status) {
          continue;
        }
      } catch (error) {
        // Continue to next attempt
        continue;
      }
    }

    // Final fallback: try entertainment/hiburan content
    try {
      const fallbackParams: NewsParams = {
        sort_by: "popular",
        page,
        per_page: Math.min(perPage, 10), // Limit fallback results
      };
      if (locationSlug && locationSlug !== "all") {
        fallbackParams.location = locationSlug;
      }
      const fallbackResult = await this.fetchNews(fallbackParams);
      if (fallbackResult?.data && fallbackResult.data.length > 0) {
        return {
          ...fallbackResult,
          message: "Celebrity news not available, showing popular news",
        };
      }
    } catch (error) {
      console.warn("Celebrity news fallback failed:", error);
    }

    // If all attempts fail, return empty result
    return {
      status: true,
      message: "No celebrity news found",
      data: [],
      meta: {
        total: 0,
        per_page: perPage,
        current_page: page,
        last_page: 1,
      },
    };
  }

  /**
   * Fetch most-seen news (sorted by views)
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with most-seen news
   */
  static async fetchMostSeeNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      sort_by: "views",
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    return this.fetchNews(params);
  }

  /**
   * Fetch trending news (combination of recent, popular, and most-viewed)
   *
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @returns Promise with trending news
   */
  static async fetchTrendingNews(
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      sort_by: "trending",
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    return this.fetchNews(params);
  }

  /**
   * Fetch news by category
   *
   * @param categorySlug Category slug
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @param sortBy Optional sorting method
   * @returns Promise with news filtered by category
   */
  static async fetchNewsByCategory(
    categorySlug: string,
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
    sortBy: string = "latest",
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      category: categorySlug,
      sort_by: sortBy as any,
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    try {
      const result = await this.fetchNews(params);

      // If no results for this specific category and sort, try other sort options
      if (!result.data || result.data.length === 0) {
        const fallbackSorts = ["latest", "popular", "views"];

        for (const fallbackSort of fallbackSorts) {
          if (fallbackSort === sortBy) continue; // Skip the already tried sort

          const fallbackParams = { ...params, sort_by: fallbackSort as any };
          const fallbackResult = await this.fetchNews(fallbackParams);

          if (fallbackResult?.data && fallbackResult.data.length > 0) {
            return {
              ...fallbackResult,
              message: `No news found for ${categorySlug} sorted by ${sortBy}, showing ${fallbackSort} instead`,
            };
          }
        }
      }

      return result;
    } catch (error) {
      console.warn(`Category news fetch failed for ${categorySlug}:`, error);
      return {
        status: false,
        message: `Failed to fetch news for category: ${categorySlug}`,
        data: [],
        meta: {
          total: 0,
          per_page: perPage,
          current_page: page,
          last_page: 1,
        },
      };
    }
  }

  /**
   * Fetch news by tag
   *
   * @param tagSlug Tag slug
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @param sortBy Optional sorting method
   * @returns Promise with news filtered by tag
   */
  static async fetchNewsByTag(
    tagSlug: string,
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
    sortBy: string = "latest",
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      tag: tagSlug,
      sort_by: sortBy as any,
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    return this.fetchNews(params);
  }

  /**
   * Search news
   *
   * @param searchTerm Search term
   * @param page Page number
   * @param perPage Items per page
   * @param locationSlug Optional location slug filter
   * @param sortBy Optional sorting method
   * @returns Promise with search results
   */
  static async searchNews(
    searchTerm: string,
    page: number = 1,
    perPage: number = 20,
    locationSlug?: string,
    sortBy: string = "latest",
  ): Promise<PaginatedResponse<News>> {
    const params: NewsParams = {
      search: searchTerm,
      sort_by: sortBy as any,
      page,
      per_page: perPage,
    };

    if (locationSlug && locationSlug !== "all") {
      params.location = locationSlug;
    }

    // Don't use cache for search
    return this.fetchNews(params, false);
  }

  /**
   * Get news detail by slug
   *
   * @param slug News slug
   * @returns Promise with news detail
   */
  static async getNewsBySlug(slug: string): Promise<News> {
    const url = `${this.API_URL}/${slug}`;

    // Use a special cache key for individual news items
    const cacheKey = `detail`;
    const params = { slug };

    // Try to get from cache first
    const cachedData = CacheHelper.getCachedNews<{
      status: boolean;
      data: News;
    }>(cacheKey, params);
    if (cachedData) {
      return cachedData.data;
    }

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Error fetching news detail: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Cache the result if successful
      if (data.status) {
        CacheHelper.cacheNews(cacheKey, params, data, {
          ttl: this.DEFAULT_CACHE_TTL,
        });
      }

      return data.data;
    } catch (error) {
      console.error("Error fetching news detail:", error);
      throw error;
    }
  }

  /**
   * Get news detail by ID or user input (legacy support)
   * @deprecated Use getNewsBySlug instead
   */
  static async getNewsById(id: number | string): Promise<News> {
    // For backward compatibility, try to convert ID to slug
    // This is a fallback method
    console.warn("getNewsById is deprecated, use getNewsBySlug instead");

    // Convert user input to slug format
    const slug = toSlug(String(id));
    return this.getNewsBySlug(slug);
  }

  /**
   * Smart news fetcher - accepts either slug or user input and converts appropriately
   *
   * @param input News slug or user input (title, ID, etc.)
   * @returns Promise with news detail
   */
  static async getNewsByInput(input: string): Promise<News> {
    // Convert any user input to proper slug format
    const slug = toSlug(input);
    return this.getNewsBySlug(slug);
  }

  /**
   * Invalidate all news caches
   */
  static invalidateAllCaches(): void {
    CacheHelper.invalidateNewsCache();
  }

  /**
   * Invalidate specific news type cache
   *
   * @param type News type or sort_by value to invalidate
   */
  static invalidateTypeCache(type: string): void {
    CacheHelper.invalidateNewsCache(type);
  }
}

export default NewsService;
